import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { Decimal } from "@/libs/decimal";
import { TokenDto } from "@/api/generated";
import { useAeBalance } from "@/composables";

import { useAccounts } from "./accounts";

export const useTokenTradeStore = defineStore("tokenTradeStore", () => {
  const { slippage } = storeToRefs(useAccounts());
  const { aeBalance } = useAeBalance();

  const token = ref<TokenDto>();
  const userBalance = ref<string | undefined>();

  const loadingAePrice = ref(false);
  const loadingTransaction = ref(false);
  const desiredSlippage = ref(0);

  const isBuying = ref(true);
  const isAllowSelling = ref(false);

  const successTxData = ref<{
    isBuying: boolean;
    destAmount: Decimal;
    sourceAmount: Decimal;
    symbol: string;
    protocolReward?: Decimal;
    protocolSymbol?: string;
    userBalance: Decimal;
  }>();

  const averageTokenPrice = computed<Decimal>(() => {
    if (!tokenA.value || !tokenB.value || tokenA.value <= 0) {
      return isBuying.value
        ? Decimal.from(token.value?.price || 0)
        : Decimal.from(token.value?.sell_price || 0);
    }
    const _tokenA = Decimal.from(tokenA.value);
    const _tokenB = Decimal.from(tokenB.value);
    return isBuying.value ? _tokenA.div(_tokenB) : _tokenB.div(_tokenA);
  });

  const currentTokenUnitPrice = computed(() => {
    return Decimal.from(token.value?.price || 0);
  });

  /**
   * The token A and token B values are used to store the token values for the trade.
   */
  const tokenA = ref<number>();
  const tokenB = ref<number>();
  const tokenAFocused = ref<boolean>(false);
  const nextPrice = ref<Decimal>(Decimal.ZERO);

  const priceImpactDiff = computed(() => {
    if (!tokenA.value || !tokenB.value || tokenA.value <= 0) {
      return Decimal.ZERO;
    }
    const diff = nextPrice.value.sub(currentTokenUnitPrice.value);
    if (diff.lt(0) || !isBuying.value) {
      return currentTokenUnitPrice.value.sub(
        Decimal.fromBigNumberString(nextPrice.value.bigNumber)
      );
    }
    return diff;
  });

  const priceImpactPercent = computed(() => {
    if (nextPrice.value.isZero || currentTokenUnitPrice.value.isZero) {
      return Decimal.ZERO;
    }

    // Calculate the percentage difference
    const percentageDifference = priceImpactDiff.value
      .div(currentTokenUnitPrice.value)
      .mul(100);

    return percentageDifference; // Return result with two decimal places
  });

  const estimatedNextTokenPriceImpactDifferenceFormattedPercentage = computed(
    () => {
      return priceImpactPercent.value.gt(Decimal.from(1))
        ? priceImpactPercent.value.prettify(2)
        : priceImpactPercent.value.prettify();
    }
  );

  const spendableAeBalance = computed((): Decimal => {
    if (aeBalance.value.isZero) {
      return Decimal.ZERO;
    }
    const slippageAmount = aeBalance.value
      .mul(Decimal.from(slippage.value).add(1))
      .div(100);
    const balance = aeBalance.value
      .sub(slippageAmount)
      .sub(Decimal.from("0.001"));

    if (balance.lte(Decimal.ZERO)) {
      return Decimal.ZERO;
    }
    return balance;
  });

  const isInsufficientBalance = computed(() =>
    isBuying.value
      ? Decimal.from(tokenA.value || 0).gt(spendableAeBalance.value)
      : Decimal.from(tokenB.value || 0).gt(Decimal.from(userBalance.value || 0))
  );

  /**
   * Switches the active trade view and updates the token usage state.
   */
  function switchTradeView(state: boolean) {
    isBuying.value = state;
  }

  function toggleTradeView() {
    isBuying.value = !isBuying.value;
  }

  return {
    tokenA,
    tokenB,
    tokenAFocused,
    averageTokenPrice,
    desiredSlippage,
    token,
    currentTokenUnitPrice,
    isBuying,
    isAllowSelling,
    loadingAePrice,
    loadingTransaction,
    nextPrice,
    priceImpactDiff,
    priceImpactPercent,
    estimatedNextTokenPriceImpactDifferenceFormattedPercentage,
    userBalance,
    successTxData,
    spendableAeBalance,
    isInsufficientBalance,

    toggleTradeView,
    switchTradeView,
  };
});
